import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  ArrowRight,
  Bot,
  Check,
  CircleSlash2,
  Clock3,
  Code2,
  FileKey2,
  Plus,
  RadioTower,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  TerminalSquare,
  Undo2,
} from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  apiClient,
  type Project,
  type RuntimeCapabilities,
  type TransmissionApplication,
  type TransmissionAuditEntry,
  type TransmissionCallsite,
  type TransmissionRelease,
} from '../lib/api-client';
import { useToast } from '../hooks/useToast';
import { useAuthStore } from '../store/authStore';

type Panel = 'callsites' | 'audit';
type DecisionKind = 'approve' | 'reject' | 'dismiss' | 'suppress' | 'allow' | 'revoke';

interface DecisionState {
  kind: DecisionKind;
  callsite: TransmissionCallsite;
}

function formatNumber(value: number | string | null | undefined): string {
  return Number(value || 0).toLocaleString();
}

function formatBytes(value: number | string | null | undefined): string {
  const bytes = Number(value || 0);
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index > 1 ? 1 : 0)} ${units[index]}`;
}

function formatDate(value?: number | null): string {
  if (!value) return 'Unknown time';
  return new Date(value * 1000).toLocaleString();
}

function levelTone(level: TransmissionCallsite['declared_level']): string {
  if (level === 'fatal' || level === 'error') return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300';
  if (level === 'warn') return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300';
  return 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300';
}

export const TransmissionControlPage: React.FC = () => {
  const [applications, setApplications] = useState<TransmissionApplication[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [capabilities, setCapabilities] = useState<RuntimeCapabilities | null>(null);
  const [releases, setReleases] = useState<TransmissionRelease[]>([]);
  const [callsites, setCallsites] = useState<TransmissionCallsite[]>([]);
  const [audit, setAudit] = useState<TransmissionAuditEntry[]>([]);
  const [applicationId, setApplicationId] = useState('');
  const [version, setVersion] = useState('');
  const [panel, setPanel] = useState<Panel>('callsites');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState<DecisionState | null>(null);
  const [reason, setReason] = useState('');
  const [acknowledgeHighSeverity, setAcknowledgeHighSeverity] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [applicationForm, setApplicationForm] = useState({
    project_id: '',
    application_id: '',
    name: '',
    platform: 'node',
  });
  const { success, error: showError } = useToast();
  const role = useAuthStore((state) => state.user?.role);
  const canManage = role === 'owner' || role === 'admin';
  const aiAvailable = capabilities?.features.ai_recommendations.available === true;

  useEffect(() => {
    void loadApplications();
  }, []);

  useEffect(() => {
    if (!applicationId) {
      setReleases([]);
      setVersion('');
      return;
    }
    void loadApplication(applicationId);
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId || !version) {
      setCallsites([]);
      setAudit([]);
      return;
    }
    void loadRelease(applicationId, version);
  }, [applicationId, version]);

  const currentApplication = applications.find((application) => application.application_id === applicationId);
  const currentRelease = releases.find((release) => release.version === version);
  const totals = useMemo(() => callsites.reduce((result, callsite) => ({
    pending: result.pending + (callsite.recommendation_status === 'pending' ? 1 : 0),
    suppressed: result.suppressed + (callsite.effective_action === 'suppress' ? 1 : 0),
    attempts: result.attempts + Number(callsite.suppressed_attempts || 0),
    bytes: result.bytes + Number(callsite.suppressed_bytes_estimate || 0),
  }), { pending: 0, suppressed: 0, attempts: 0, bytes: 0 }), [callsites]);
  const visibleCallsites = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return callsites;
    return callsites.filter((callsite) =>
      callsite.callsite_id.toLowerCase().includes(query) ||
      callsite.declared_level?.includes(query) ||
      callsite.suggested_level?.includes(query) ||
      callsite.semantic_type?.includes(query) ||
      callsite.recommendation_reason?.toLowerCase().includes(query)
    );
  }, [callsites, search]);

  async function loadApplications(preferredApplicationId?: string) {
    setLoading(true);
    try {
      const [applicationResponse, projectResponse, capabilityResponse] = await Promise.all([
        apiClient.getTransmissionApplications(),
        apiClient.getProjects(),
        apiClient.getCapabilities(),
      ]);
      const nextApplications = applicationResponse.applications || [];
      setApplications(nextApplications);
      setProjects(projectResponse.projects || []);
      setCapabilities(capabilityResponse);
      const nextId = preferredApplicationId || applicationId || nextApplications[0]?.application_id || '';
      setApplicationId(nextId);
      if (!applicationForm.project_id && projectResponse.projects?.[0]) {
        setApplicationForm((current) => ({ ...current, project_id: projectResponse.projects[0].id }));
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to load transmission control');
    } finally {
      setLoading(false);
    }
  }

  async function loadApplication(id: string) {
    try {
      const response = await apiClient.getTransmissionApplication(id);
      const nextReleases = response.releases || [];
      setReleases(nextReleases);
      setVersion((current) => nextReleases.some((release) => release.version === current)
        ? current
        : nextReleases[0]?.version || '');
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to load application releases');
    }
  }

  async function loadRelease(id = applicationId, selectedVersion = version, quiet = false) {
    if (!id || !selectedVersion) return;
    if (!quiet) setLoading(true);
    else setRefreshing(true);
    try {
      const [callsiteResponse, auditResponse] = await Promise.all([
        apiClient.getTransmissionCallsites(id, selectedVersion),
        apiClient.getTransmissionAudit(id, selectedVersion),
      ]);
      setCallsites(callsiteResponse.callsites || []);
      setAudit(auditResponse.audit || []);
      setReleases((current) => current.map((release) =>
        release.version === selectedVersion ? { ...release, ...callsiteResponse.release } : release
      ));
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to load release controls');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function createApplication() {
    if (!applicationForm.project_id || !applicationForm.application_id.trim() || !applicationForm.name.trim()) return;
    setBusy(true);
    try {
      const response = await apiClient.createTransmissionApplication({
        ...applicationForm,
        application_id: applicationForm.application_id.trim(),
        name: applicationForm.name.trim(),
      });
      success('Application created. Upload its first opaque manifest from your build.');
      setShowCreate(false);
      setApplicationForm((current) => ({ ...current, application_id: '', name: '' }));
      await loadApplications(response.application.application_id);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to create application');
    } finally {
      setBusy(false);
    }
  }

  async function runAiReview() {
    if (!applicationId || !version) return;
    setBusy(true);
    try {
      const response = await apiClient.analyzeTransmissionRecommendations(applicationId, version);
      success(response.recommendations.length
        ? `${response.recommendations.length} AI suggestion${response.recommendations.length === 1 ? '' : 's'} ready for user review.`
        : `AI reviewed ${response.candidates_analyzed} call site${response.candidates_analyzed === 1 ? '' : 's'} and suggested no changes.`);
      await loadRelease(applicationId, version, true);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'AI review failed');
    } finally {
      setBusy(false);
    }
  }

  function openDecision(kind: DecisionKind, callsite: TransmissionCallsite) {
    setDecision({ kind, callsite });
    setReason('');
    setAcknowledgeHighSeverity(false);
  }

  async function submitDecision() {
    if (!decision || !reason.trim() || !applicationId || !version) return;
    setBusy(true);
    try {
      const { kind, callsite } = decision;
      if (kind === 'approve' && callsite.recommendation_id) {
        await apiClient.decideTransmissionRecommendation(applicationId, version, callsite.recommendation_id, {
          action: callsite.recommended_action === 'suppress' ? 'approve_suppression' : 'approve_allow',
          reason: reason.trim(),
          acknowledge_high_severity: acknowledgeHighSeverity,
        });
      } else if ((kind === 'reject' || kind === 'dismiss') && callsite.recommendation_id) {
        await apiClient.decideTransmissionRecommendation(applicationId, version, callsite.recommendation_id, {
          action: kind,
          reason: reason.trim(),
        });
      } else if (kind === 'suppress' || kind === 'allow') {
        await apiClient.setTransmissionDecision(applicationId, version, callsite.callsite_id, {
          action: kind,
          reason: reason.trim(),
          acknowledge_high_severity: acknowledgeHighSeverity,
        });
      } else if (kind === 'revoke') {
        await apiClient.revokeTransmissionDecision(applicationId, version, callsite.callsite_id, reason.trim());
      }
      success(decisionSuccessLabel(kind));
      setDecision(null);
      await Promise.all([loadRelease(applicationId, version, true), loadApplication(applicationId)]);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to update transmission control');
    } finally {
      setBusy(false);
    }
  }

  const decisionRequiresHighSeverityAck = Boolean(
    decision &&
    (decision.kind === 'suppress' ||
      (decision.kind === 'approve' && decision.callsite.recommended_action === 'suppress')) &&
    ['error', 'fatal'].includes(decision.callsite.declared_level || '')
  );

  return (
    <AppShell>
      <div className="space-y-7">
        <header className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950/50 dark:text-indigo-300">
              <RadioTower className="h-3.5 w-3.5" /> Transmission control
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-950 dark:text-white">Keep the local signal. Control the cloud cost.</h1>
            <p className="mt-2 text-base text-muted-foreground">
              {aiAvailable
                ? 'AI can propose a change. Only a user can activate what this exact application version sends to LogLimit.'
                : 'Only a user can change what this exact application version sends to LogLimit. Optional AI review is not configured yet.'}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select
              aria-label="Application"
              value={applicationId}
              onChange={(event) => setApplicationId(event.target.value)}
              options={applications.map((application) => ({ value: application.application_id, label: application.name }))}
              className="min-w-48"
            />
            <Select
              aria-label="Application version"
              value={version}
              onChange={(event) => setVersion(event.target.value)}
              disabled={!releases.length}
              options={releases.map((release) => ({ value: release.version, label: `v${release.version}` }))}
              className="min-w-36 font-mono"
            />
            <Button variant="outline" size="icon" onClick={() => void loadRelease(applicationId, version, true)} disabled={!version || refreshing} aria-label="Refresh release">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            {canManage && version && aiAvailable && <Button variant="outline" onClick={() => void runAiReview()} disabled={busy} className="gap-2"><Bot className="h-4 w-4" /> {busy ? 'Reviewing…' : 'Run AI review'}</Button>}
            {canManage && projects.length > 0 && <Button onClick={() => setShowCreate(true)} className="gap-2"><Plus className="h-4 w-4" /> Application</Button>}
          </div>
        </header>

        {!applicationId && !loading ? (
          <Card className="border-dashed border-indigo-300 bg-white/85 py-12 text-center dark:border-indigo-800 dark:bg-slate-950/70">
            <CardContent className="mx-auto max-w-xl p-6">
              <Code2 className="mx-auto h-10 w-10 text-indigo-600" />
              <h2 className="mt-4 text-xl font-semibold">{projects.length ? 'Create the application identity' : 'Start with a project connection'}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{projects.length
                ? 'Connect it to a project, then let the Node build tool upload the first opaque call-site manifest.'
                : 'A project creates the public connection key used by the Node SDK. Then return here to register the application and its first release.'}</p>
              {canManage && projects.length > 0 && <Button className="mt-6 gap-2" onClick={() => setShowCreate(true)}><Plus className="h-4 w-4" /> Create application</Button>}
              {canManage && projects.length === 0 && <a href="/projects" className="mt-6 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90">Create the first project <ArrowRight className="ml-2 h-4 w-4" /></a>}
            </CardContent>
          </Card>
        ) : applicationId && !version && !loading ? (
          <Card className="overflow-hidden border-indigo-200 bg-white/90 dark:border-indigo-900 dark:bg-slate-950/70">
            <CardContent className="grid gap-6 p-6 md:grid-cols-[auto_1fr] md:items-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"><FileKey2 className="h-7 w-7" /></div>
              <div><h2 className="text-lg font-semibold">Waiting for the first public manifest</h2><p className="mt-1 text-sm text-muted-foreground">Run <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs dark:bg-slate-900">loglimit-manifest --upload</code> in CI. Plaintext source paths remain in your private artifact.</p></div>
            </CardContent>
          </Card>
        ) : version ? (
          <>
            <section className="overflow-hidden rounded-2xl border border-white/80 bg-white/90 shadow-sm backdrop-blur dark:border-white/10 dark:bg-slate-950/75" aria-label="Transmission path">
              <div className="grid lg:grid-cols-[1fr_auto_1fr] lg:items-stretch">
                <div className="flex gap-4 p-5 sm:p-6">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"><TerminalSquare className="h-5 w-5" /></div>
                  <div><p className="text-xs font-semibold uppercase tracking-[0.15em] text-emerald-700 dark:text-emerald-300">Local sinks</p><h2 className="mt-1 text-lg font-semibold">Console and files continue</h2><p className="mt-1 text-sm text-muted-foreground">Every native logger call runs with its original arguments and return value.</p></div>
                </div>
                <div className="hidden items-center px-2 text-indigo-500 lg:flex"><ArrowRight className="h-6 w-6" /></div>
                <div className="flex gap-4 border-t border-indigo-100 bg-indigo-50/70 p-5 dark:border-indigo-950 dark:bg-indigo-950/20 sm:p-6 lg:border-l lg:border-t-0">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"><ShieldCheck className="h-5 w-5" /></div>
                  <div><div className="flex flex-wrap items-center gap-2"><p className="text-xs font-semibold uppercase tracking-[0.15em] text-indigo-700 dark:text-indigo-300">LogLimit queue</p><span className="rounded-full border border-indigo-200 bg-white px-2 py-0.5 font-mono text-[11px] text-indigo-700 dark:border-indigo-900 dark:bg-slate-950 dark:text-indigo-300">config v{currentRelease?.config_version ?? 0}</span></div><h2 className="mt-1 text-lg font-semibold">{totals.suppressed} call site{totals.suppressed === 1 ? '' : 's'} stopped before enqueue</h2><p className="mt-1 text-sm text-muted-foreground">Rules apply only to <span className="font-mono">{currentApplication?.application_id}@{version}</span>.</p></div>
                </div>
              </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Transmission summary">
              <SummaryCard icon={<Sparkles className="h-5 w-5" />} tone="indigo" value={formatNumber(totals.pending)} label="Proposals awaiting a user" />
              <SummaryCard icon={<CircleSlash2 className="h-5 w-5" />} tone="rose" value={formatNumber(totals.suppressed)} label="Active version-scoped suppressions" />
              <SummaryCard icon={<Activity className="h-5 w-5" />} tone="amber" value={formatNumber(totals.attempts)} label="SDK-counted suppressed attempts" />
              <SummaryCard icon={<ShieldCheck className="h-5 w-5" />} tone="emerald" value={formatBytes(totals.bytes)} label="Estimated transmission avoided" />
            </section>

            <div className="flex flex-col gap-4 border-b pb-4 md:flex-row md:items-center md:justify-between">
              <div className="flex gap-1 rounded-lg border bg-white/80 p-1 dark:bg-slate-950/70">
                <PanelButton active={panel === 'callsites'} onClick={() => setPanel('callsites')}><Code2 className="h-4 w-4" /> Call sites</PanelButton>
                <PanelButton active={panel === 'audit'} onClick={() => setPanel('audit')}><Clock3 className="h-4 w-4" /> User activity</PanelButton>
              </div>
              {panel === 'callsites' && <div className="relative w-full md:max-w-sm"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Find an opaque ID, level, or reason" className="pl-9" /></div>}
            </div>

            {loading ? <LoadingRows /> : panel === 'callsites' ? (
              visibleCallsites.length ? <div className="space-y-3">{visibleCallsites.map((callsite) => (
                <CallsiteRow key={callsite.callsite_id} callsite={callsite} canManage={canManage} onDecision={openDecision} />
              ))}</div> : <EmptyState icon={<Search className="h-6 w-6" />} title="No call sites match" body="Clear the search or upload a manifest containing instrumented logger calls." />
            ) : audit.length ? (
              <Card className="overflow-hidden bg-white/90 dark:bg-slate-950/70"><CardContent className="divide-y p-0">{audit.map((entry) => <AuditRow key={entry.id} entry={entry} />)}</CardContent></Card>
            ) : <EmptyState icon={<Clock3 className="h-6 w-6" />} title="No user decisions yet" body="Recommendations never change transmission. Activity appears only after a person decides." />}
          </>
        ) : null}
      </div>

      <Dialog isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create transmission application">
        <div className="space-y-4">
          {!projects.length && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Create a project and its public connection key before adding an application.</div>}
          <Select label="Project" value={applicationForm.project_id} onChange={(event) => setApplicationForm({ ...applicationForm, project_id: event.target.value })} options={projects.map((project) => ({ value: project.id, label: project.name }))} />
          <InputField label="Application ID" hint="Stable identifier used by SDKs and build tooling."><Input value={applicationForm.application_id} onChange={(event) => setApplicationForm({ ...applicationForm, application_id: event.target.value })} placeholder="checkout-api" /></InputField>
          <InputField label="Display name"><Input value={applicationForm.name} onChange={(event) => setApplicationForm({ ...applicationForm, name: event.target.value })} placeholder="Checkout API" /></InputField>
          <Select label="First SDK" value={applicationForm.platform} onChange={(event) => setApplicationForm({ ...applicationForm, platform: event.target.value })} options={[{ value: 'node', label: 'Node.js / TypeScript' }]} />
          <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button disabled={busy || !applicationForm.project_id || !applicationForm.application_id.trim() || !applicationForm.name.trim()} onClick={() => void createApplication()}>{busy ? 'Creating…' : 'Create application'}</Button></div>
        </div>
      </Dialog>

      <Dialog isOpen={Boolean(decision)} onClose={() => setDecision(null)} title={decision ? decisionTitle(decision) : undefined}>
        {decision && <div className="space-y-4">
          <div className="rounded-xl border bg-slate-50 p-4 dark:bg-slate-900/60"><div className="flex items-center justify-between gap-3"><code className="truncate font-mono text-xs">{decision.callsite.callsite_id}</code><span className={`rounded-full border px-2 py-0.5 text-xs font-semibold uppercase ${levelTone(decision.callsite.declared_level)}`}>{decision.callsite.declared_level || 'unknown'}</span></div>{decision.callsite.recommendation_reason && <p className="mt-3 text-sm text-muted-foreground">{decision.callsite.recommendation_reason}</p>}</div>
          {decisionRequiresHighSeverityAck && <label className="flex cursor-pointer gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-950 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-100"><input type="checkbox" checked={acknowledgeHighSeverity} onChange={(event) => setAcknowledgeHighSeverity(event.target.checked)} className="mt-0.5 h-4 w-4" /><span><strong>High-severity acknowledgement</strong><span className="mt-1 block text-rose-700 dark:text-rose-300">I understand this stops only LogLimit transmission for this error/fatal call site. Local logging still runs.</span></span></label>}
          <InputField label="Decision reason" hint="Stored in the immutable audit trail."><textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" placeholder="Why is this correct for this application version?" /></InputField>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setDecision(null)}>Cancel</Button><Button disabled={busy || !reason.trim() || (decisionRequiresHighSeverityAck && !acknowledgeHighSeverity)} variant={decision.kind === 'suppress' || (decision.kind === 'approve' && decision.callsite.recommended_action === 'suppress') ? 'destructive' : 'default'} onClick={() => void submitDecision()}>{busy ? 'Applying…' : decisionActionLabel(decision)}</Button></div>
        </div>}
      </Dialog>
    </AppShell>
  );
};

function SummaryCard({ icon, tone, value, label }: { icon: React.ReactNode; tone: 'indigo' | 'rose' | 'amber' | 'emerald'; value: string; label: string }) {
  const tones = {
    indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
    rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
    emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  };
  return <Card className="border-white/80 bg-white/90 dark:border-white/10 dark:bg-slate-950/70"><CardContent className="flex gap-4 p-5"><div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}>{icon}</div><div><p className="text-xl font-bold tabular-nums">{value}</p><p className="mt-0.5 text-xs leading-5 text-muted-foreground">{label}</p></div></CardContent></Card>;
}

function PanelButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? 'bg-slate-950 text-white shadow-sm dark:bg-white dark:text-slate-950' : 'text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-900'}`}>{children}</button>;
}

function CallsiteRow({ callsite, canManage, onDecision }: { callsite: TransmissionCallsite; canManage: boolean; onDecision: (kind: DecisionKind, callsite: TransmissionCallsite) => void }) {
  const pending = callsite.recommendation_status === 'pending';
  const suppressionActive = callsite.effective_action === 'suppress';
  return <Card className={`overflow-hidden bg-white/90 dark:bg-slate-950/70 ${pending ? 'border-indigo-200 dark:border-indigo-900' : suppressionActive ? 'border-rose-200 dark:border-rose-900' : 'border-white/80 dark:border-white/10'}`}>
    <CardContent className="p-0">
      <div className="grid gap-5 p-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(260px,.8fr)_auto] xl:items-center">
        <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase ${levelTone(callsite.declared_level)}`}>{callsite.declared_level || 'unknown'}</span><code className="truncate font-mono text-xs text-muted-foreground">#{callsite.runtime_index} · {callsite.callsite_id}</code></div><div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground"><span>{formatNumber(callsite.transmitted_count)} transmitted</span><span>{formatNumber(callsite.suppressed_attempts)} suppressed attempts</span><span>{formatBytes(callsite.suppressed_bytes_estimate)} avoided</span><span>{formatNumber(callsite.distinct_installations_estimate)} installations</span></div></div>
        <div>{pending ? <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3 dark:border-indigo-900 dark:bg-indigo-950/30"><div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-indigo-700 dark:text-indigo-300"><Bot className="h-3.5 w-3.5" /> {callsite.recommendation_type === 'carryover' ? 'Carryover suggestion' : 'AI suggestion'} · {Math.round(Number(callsite.confidence || 0) * 100)}%</div><div className="mt-2 flex flex-wrap gap-1.5 text-[11px]"><span className="rounded-full bg-white/80 px-2 py-0.5 dark:bg-slate-900">{callsite.semantic_type || 'unknown type'}</span><span className="rounded-full bg-white/80 px-2 py-0.5 dark:bg-slate-900">{callsite.value_assessment || 'unknown value'} value</span>{callsite.suggested_level && callsite.suggested_level !== callsite.declared_level && <span className="rounded-full bg-white/80 px-2 py-0.5 dark:bg-slate-900">level: {callsite.declared_level || 'unknown'} → {callsite.suggested_level}</span>}</div><p className="mt-1.5 text-sm text-slate-800 dark:text-slate-200">{callsite.recommendation_reason}</p><p className="mt-1 text-[11px] text-muted-foreground">Inactive until a user approves.</p></div> : <div className={`rounded-xl border p-3 ${suppressionActive ? 'border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/30' : 'bg-slate-50 dark:bg-slate-900/60'}`}><p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Effective rule</p><p className="mt-1 text-sm font-semibold">{suppressionActive ? 'Do not send to LogLimit' : callsite.effective_action === 'allow' ? 'Explicitly send to LogLimit' : 'Default: send to LogLimit'}</p></div>}</div>
        <div className="flex flex-wrap justify-start gap-2 xl:max-w-64 xl:justify-end">{canManage ? pending ? <><Button size="sm" onClick={() => onDecision('approve', callsite)} disabled={callsite.recommended_action === 'review'} className="gap-1.5"><Check className="h-3.5 w-3.5" /> Approve {callsite.recommended_action}</Button><Button size="sm" variant="outline" onClick={() => onDecision('dismiss', callsite)}>Dismiss</Button></> : suppressionActive ? <Button size="sm" variant="outline" onClick={() => onDecision('revoke', callsite)} className="gap-1.5"><Undo2 className="h-3.5 w-3.5" /> Restore default</Button> : <Button size="sm" variant="outline" onClick={() => onDecision('suppress', callsite)} className="gap-1.5 text-rose-700 dark:text-rose-300"><CircleSlash2 className="h-3.5 w-3.5" /> Stop cloud send</Button> : <span className="text-xs text-muted-foreground">View only</span>}</div>
      </div>
    </CardContent>
  </Card>;
}

function AuditRow({ entry }: { entry: TransmissionAuditEntry }) {
  return <div className="grid gap-3 p-5 md:grid-cols-[auto_1fr_auto] md:items-start"><div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-300"><Clock3 className="h-4 w-4" /></div><div><div className="flex flex-wrap items-center gap-2"><p className="text-sm font-semibold">{auditActionLabel(entry.action)}</p><code className="font-mono text-[11px] text-muted-foreground">{entry.callsite_id}</code></div><p className="mt-1 text-sm text-muted-foreground">{entry.reason}</p><p className="mt-2 text-xs text-muted-foreground">{entry.actor_email || entry.actor_user_id}</p></div><time className="text-xs text-muted-foreground">{formatDate(entry.created_at)}</time></div>;
}

function EmptyState({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return <Card className="border-dashed bg-white/80 py-10 text-center dark:bg-slate-950/60"><CardContent className="mx-auto max-w-lg p-6"><div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-300">{icon}</div><h2 className="mt-4 font-semibold">{title}</h2><p className="mt-1 text-sm text-muted-foreground">{body}</p></CardContent></Card>;
}

function LoadingRows() {
  return <div className="space-y-3" aria-label="Loading call sites">{[0, 1, 2].map((row) => <div key={row} className="h-32 animate-pulse rounded-xl bg-white/70 dark:bg-slate-900/60" />)}</div>;
}

function InputField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return <label className="block"><span className="mb-1 block text-sm font-medium">{label}</span>{children}{hint && <span className="mt-1 block text-xs text-muted-foreground">{hint}</span>}</label>;
}

function decisionTitle(decision: DecisionState): string {
  if (decision.kind === 'approve') return `Approve AI ${decision.callsite.recommended_action} suggestion`;
  if (decision.kind === 'revoke') return 'Restore default transmission';
  if (decision.kind === 'suppress') return 'Stop sending this call site';
  if (decision.kind === 'allow') return 'Allow this call site';
  return `${decision.kind === 'reject' ? 'Reject' : 'Dismiss'} AI suggestion`;
}

function decisionActionLabel(decision: DecisionState): string {
  if (decision.kind === 'approve') return `Approve ${decision.callsite.recommended_action}`;
  if (decision.kind === 'revoke') return 'Restore default';
  if (decision.kind === 'suppress') return 'Stop cloud send';
  if (decision.kind === 'allow') return 'Allow cloud send';
  return decision.kind === 'reject' ? 'Reject suggestion' : 'Dismiss suggestion';
}

function decisionSuccessLabel(kind: DecisionKind): string {
  if (kind === 'revoke') return 'Default transmission restored.';
  if (kind === 'suppress') return 'Cloud transmission stopped for this version.';
  if (kind === 'allow') return 'Cloud transmission allowed for this version.';
  if (kind === 'approve') return 'AI suggestion approved by user.';
  return 'AI suggestion closed without changing transmission.';
}

function auditActionLabel(action: TransmissionAuditEntry['action']): string {
  const labels: Record<TransmissionAuditEntry['action'], string> = {
    approve_suppression: 'Stopped LogLimit transmission',
    approve_allow: 'Allowed LogLimit transmission',
    reject: 'Rejected AI suggestion',
    dismiss: 'Dismissed AI suggestion',
    revoke: 'Restored default transmission',
    replace_manifest: 'Replaced release manifest',
  };
  return labels[action];
}
